import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '../context/AutomaticFormContext.tsx';
import { SelectOrganizationInput } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { useFormLocations } from '../hooks/useFormLocations.ts';
import { useOrderFormQuantities } from '../hooks/useOrderFormQuantities.ts';
import { NegativePurchasePriceHint, ZeroPurchasePriceHint } from '@/shared/components/PriceInputHints.tsx';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { UnitInput } from '@/shared/inputs/UnitInput.tsx';
import { PriceInput } from '@/shared/inputs/PriceInput.tsx';
import { TaxClassInput } from '@/shared/inputs/TaxClassInput.tsx';

export function OrderPurchaseMaterialFieldset() {
    const methods = useFormContext<AutomaticProductsFormValues>();
    const { t } = useTranslation();
    const { taxClassOptions, defaultValuesLabels, customerOrganization, formRules } = useAutomaticFormContext();
    const {
        register,
        watch,
        setValue,
        getValues,
        formState: { defaultValues },
    } = methods;
    const { clearLocations } = useFormLocations(methods);
    const { onPurchaseMaterialUnitChange } = useOrderFormQuantities(methods);

    const [
        purchaseMaterialPrice,
        purchaseMaterialUnit,
        currencyCode,
        showZeroPurchaseMaterialPrice,
        isPurchaseTransportIncluded,
    ] = watch([
        'purchaseMaterialPrice',
        'purchaseMaterialUnit',
        'currencyCode',
        'showZeroPurchaseMaterialPrice',
        'isPurchaseTransportIncluded',
    ]);

    const [supplierOrganizationName, setSupplierOrganizationName] = useState<string | undefined>(
        defaultValuesLabels?.supplierOrganization
    );

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.materialPurchase')}
            dataTest="order-purchase-material-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-3 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn
                    className="@2xl:col-span-4 col-span-full"
                    label={t('forms.automaticProductsForm.labels.supplier')}
                >
                    <SelectOrganizationInput
                        key={defaultValues?.supplierOrganizationId}
                        {...register('supplierOrganizationId')}
                        defaultValue={defaultValues?.supplierOrganizationId}
                        defaultValueLabel={defaultValuesLabels?.supplierOrganization}
                        placeholder={t('product.addPosition.supplier.label')}
                        exclude={{ ids: [...EXCLUDED_PARTNER_ORGANIZATION_IDS, customerOrganization.id] }}
                        filters={{ organizationType: isPurchaseTransportIncluded ? 'carrier' : 'supplier' }}
                        dataTest="supplier-organization-select-input"
                        onValueChange={org => {
                            const supplierOrganizationId = getValues('supplierOrganizationId');
                            if (org && supplierOrganizationId) {
                                clearLocations(supplierOrganizationId);
                            }
                            setSupplierOrganizationName(org?.name);
                        }}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="@2xl:col-span-2">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unitPrice')}</FieldsetLabel>
                    <PriceInput
                        {...register('purchaseMaterialPrice')}
                        unit={purchaseMaterialUnit}
                        currencyCode={currencyCode}
                        testId="purchase-material-price-input"
                    />
                </FieldsetColumn>

                <FieldsetColumn className="col-span-1">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                    <UnitInput
                        {...register('purchaseMaterialUnit')}
                        options={[...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options]}
                        disabled={formRules?.purchaseMaterialUnit?.disabled}
                        onUnitChange={unit => onPurchaseMaterialUnitChange(toAutomaticUnit(unit))}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="@2xl:col-span-1">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.taxClass')}</FieldsetLabel>
                    <TaxClassInput
                        {...register('purchaseMaterialTaxClassId')}
                        options={taxClassOptions}
                        testId="purchase-material-tax-class-input"
                    />
                </FieldsetColumn>

                {Number(purchaseMaterialPrice) === 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroPurchasePriceHint
                            partnerOrganizationName={supplierOrganizationName}
                            isBillingRelevant={showZeroPurchaseMaterialPrice ?? false}
                            onClick={() =>
                                setValue('showZeroPurchaseMaterialPrice', !(showZeroPurchaseMaterialPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(purchaseMaterialPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativePurchasePriceHint partnerOrganizationName={supplierOrganizationName} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
